/**
 * LGPD Block E — frontend Sentry gating + event scrubbing (PURE).
 *
 * Zero imports / no DOM / no env access → unit-testable in plain Node (see
 * scripts/verify-analytics-readiness.mjs). The env-/SDK-aware wrapper is in
 * `sentry.ts` and plugs `scrubSentryEvent` in as Sentry's `beforeSend`.
 *
 * Mirrors the backend scrubber (`rager/observability/sentry.py`): strip PII,
 * secrets and raw request material; preserve the error message/exception.
 */

export interface SentryConfig {
  enabled: boolean
  dsn: string
  requireConsent: boolean
}

/**
 * Whether frontend Sentry may initialize now.
 *   - env disabled            → false
 *   - no DSN                  → false
 *   - requireConsent && !consent → false (conservative default)
 */
export function sentryShouldInit(
  config: SentryConfig,
  hasAnalyticsConsent: boolean,
): boolean {
  if (!config.enabled) return false
  if (!config.dsn || config.dsn.trim().length === 0) return false
  if (config.requireConsent && hasAnalyticsConsent !== true) return false
  return true
}

const SENSITIVE_KEY =
  /(password|passwd|secret|token|api_?key|authorization|cookie|csrf|credential|dsn|session|document|content|chunk|embedding|prompt|question|email|filename|file_name)/i

const REQUEST_KEYS_TO_DROP = ['cookies', 'headers', 'data', 'env', 'query_string']
const USER_PII_KEYS = ['ip_address', 'email', 'username', 'name']
const MAX_STR = 512

type Dict = Record<string, unknown>

function isDict(v: unknown): v is Dict {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
}

function truncate(v: unknown): unknown {
  if (typeof v === 'string' && v.length > MAX_STR) {
    return v.slice(0, MAX_STR) + '...[truncated]'
  }
  return v
}

function scrubMapping(obj: Dict): void {
  for (const key of Object.keys(obj)) {
    if (SENSITIVE_KEY.test(key)) {
      obj[key] = '[redacted]'
      continue
    }
    const value = obj[key]
    if (isDict(value)) scrubMapping(value)
    else obj[key] = truncate(value)
  }
}

function scrubUrl(url: unknown): unknown {
  if (typeof url !== 'string') return url
  return url.split('?')[0]
}

/**
 * Return the event with PII/secrets/raw request material removed (mutated in
 * place and returned). Returning the event (never null) keeps the error report
 * — just sanitized.
 */
export function scrubSentryEvent(event: unknown): unknown {
  if (!isDict(event)) return event

  const req = event.request
  if (isDict(req)) {
    for (const key of REQUEST_KEYS_TO_DROP) delete req[key]
    if ('url' in req) req.url = scrubUrl(req.url)
  }

  const user = event.user
  if (isDict(user)) {
    for (const key of USER_PII_KEYS) delete user[key]
  }

  for (const section of ['extra', 'contexts', 'tags']) {
    const block = event[section]
    if (isDict(block)) scrubMapping(block)
  }

  const crumbs = event.breadcrumbs
  const values = isDict(crumbs) ? (crumbs.values as unknown) : crumbs
  if (Array.isArray(values)) {
    for (const crumb of values) {
      if (!isDict(crumb)) continue
      const data = crumb.data
      if (isDict(data)) {
        if ('url' in data) data.url = scrubUrl(data.url)
        scrubMapping(data)
      }
    }
  }

  return event
}
